import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { isApiError } from '../../api/client';
import { newTempId, useAddItem, useDeleteItem, useList, useToggleItem } from '../../api/hooks';
import type { Item } from '../../api/types';
import { displayName } from '../../lib/next';
import { Button, IconButton } from '../../ui/Button';
import { IconCart, IconChevronDown, IconChevronLeft, IconMore, IconUserPlus } from '../../ui/icons';
import { EmptyState, Skeleton } from '../../ui/Misc';
import { Page, TopBar } from '../../ui/TopBar';
import { AddItemBar } from './AddItemBar';
import { ItemRow } from './ItemRow';
import { ListOptionsSheet } from './ListOptionsSheet';
import { ShareSheet } from './ShareSheet';

export function ListDetailScreen() {
  const { id = '' } = useParams();
  const list = useList(id);
  const add = useAddItem(id);
  const toggle = useToggleItem(id);
  const del = useDeleteItem(id);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(true);

  const { open, done } = useMemo(() => {
    const items = list.data?.items ?? [];
    return { open: items.filter((i) => !i.done), done: items.filter((i) => i.done) };
  }, [list.data?.items]);

  const onToggle = (item: Item) => toggle.mutate({ itemId: item.id, done: !item.done });
  const onDelete = (item: Item) => del.mutate(item.id);

  const data = list.data;
  const isOwner = data?.role === 'owner';

  return (
    <div className="min-h-dvh pb-[calc(var(--safe-bottom)+7rem)]">
      <TopBar
        left={
          <Link
            to="/"
            aria-label="Back to lists"
            className="tap inline-grid place-items-center rounded-full text-ink hover:bg-surface-2"
          >
            <IconChevronLeft />
          </Link>
        }
        title={
          data ? (
            <h1 className="truncate text-lg font-bold tracking-tight">{data.title}</h1>
          ) : (
            <span className="sr-only">List</span>
          )
        }
        right={
          data && (
            <>
              {isOwner && (
                <IconButton label="Share list" onClick={() => setShareOpen(true)}>
                  <IconUserPlus />
                </IconButton>
              )}
              <IconButton label="List options" onClick={() => setOptionsOpen(true)}>
                <IconMore />
              </IconButton>
            </>
          )
        }
      />

      <Page className="pt-4">
        {list.isPending ? (
          <div className="space-y-2.5" aria-busy="true" aria-label="Loading items">
            <Skeleton className="mb-4 h-5 w-40" />
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 rounded-2xl" />
            ))}
          </div>
        ) : list.isError && !data ? (
          isApiError(list.error) && (list.error.status === 404 || list.error.status === 403) ? (
            <EmptyState
              icon={<IconCart size={30} />}
              title="List not found"
              action={
                <Link to="/" className="font-semibold text-accent underline-offset-4 hover:underline">
                  Back to your lists
                </Link>
              }
            >
              It may have been deleted, or you no longer have access.
            </EmptyState>
          ) : (
            <EmptyState
              icon={<IconCart size={30} />}
              title="Couldn't load this list"
              action={
                <Button variant="secondary" onClick={() => void list.refetch()} loading={list.isFetching}>
                  Try again
                </Button>
              }
            >
              {list.error.message}
            </EmptyState>
          )
        ) : data ? (
          <>
            <p className="mb-3 text-sm text-muted">
              {data.itemCount === 0
                ? 'Nothing on the list yet'
                : `${open.length} to get · ${done.length} done`}
              {data.role === 'member' && <> · shared by {displayName(data.owner)}</>}
              {data.role === 'owner' && data.members.length > 0 && (
                <>
                  {' '}
                  · shared with {data.members.length} {data.members.length === 1 ? 'person' : 'people'}
                </>
              )}
            </p>

            {data.items.length === 0 ? (
              <EmptyState icon={<IconCart size={30} />} title="No items yet">
                Add the first item with the box at the bottom of the screen.
              </EmptyState>
            ) : (
              <>
                {open.length > 0 ? (
                  <ul className="space-y-2" aria-label="To get">
                    {open.map((i) => (
                      <ItemRow key={i.id} item={i} onToggle={onToggle} onDelete={onDelete} />
                    ))}
                  </ul>
                ) : (
                  <p className="rounded-2xl bg-success-soft px-4 py-3 text-center font-medium text-success">
                    All done! 🎉
                  </p>
                )}

                {done.length > 0 && (
                  <section className="mt-6">
                    <button
                      type="button"
                      onClick={() => setDoneOpen((v) => !v)}
                      aria-expanded={doneOpen}
                      aria-controls="done-items"
                      className="tap -ml-2 flex items-center gap-1.5 rounded-xl px-2 text-sm font-semibold tracking-wide text-muted uppercase hover:text-ink"
                    >
                      <IconChevronDown
                        size={18}
                        className={`transition-transform ${doneOpen ? '' : '-rotate-90'}`}
                      />
                      Done ({done.length})
                    </button>
                    {doneOpen && (
                      <ul id="done-items" className="mt-2 space-y-2" aria-label="Done">
                        {done.map((i) => (
                          <ItemRow key={i.id} item={i} onToggle={onToggle} onDelete={onDelete} />
                        ))}
                      </ul>
                    )}
                  </section>
                )}
              </>
            )}
          </>
        ) : null}
      </Page>

      {data && <AddItemBar onAdd={(text) => add.mutate({ text, tempId: newTempId() })} />}

      {data && (
        <>
          <ListOptionsSheet
            list={data}
            open={optionsOpen}
            onClose={() => setOptionsOpen(false)}
            onShare={() => setShareOpen(true)}
          />
          {isOwner && <ShareSheet list={data} open={shareOpen} onClose={() => setShareOpen(false)} />}
        </>
      )}
    </div>
  );
}
